import { useCallback } from 'react';

interface UseDescriptionHandlersProps {
  // Callback to update the description value
  onDescriptionChange: (description: string) => void;
  // Current state of whether description field is shown
  showDescriptionField: boolean;
  // State setters for view toggling
  setShowDescriptionField: (show: boolean) => void;
  setShowDescriptionCards: (show: boolean) => void;
  // Optional: disable toggle functionality (e.g., when channel is already created)
  disabled?: boolean;
}

/**
 * Custom hook for handling description selection, editing, and view toggling.
 * Reusable across components that need description management functionality.
 * 
 * @param onDescriptionChange - Callback function to update the description value
 * @param showDescriptionField - Current state of whether description field is shown
 * @param setShowDescriptionField - State setter to show/hide the description input field
 * @param setShowDescriptionCards - State setter to show/hide the description suggestion cards
 * @param disabled - Optional flag to disable toggle functionality
 * @returns Object containing handler functions for description operations
 */
export const useDescriptionHandlers = ({
  onDescriptionChange,
  showDescriptionField,
  setShowDescriptionField,
  setShowDescriptionCards,
  disabled = false,
}: UseDescriptionHandlersProps) => {
  // Handle when a description is selected from suggestion cards
  const handleDescriptionSelect = useCallback((description: string) => {
    onDescriptionChange(description);
    setShowDescriptionField(true);
    setShowDescriptionCards(false);
  }, [onDescriptionChange, setShowDescriptionField, setShowDescriptionCards]);

  // Handle when a description is edited (same as select, but semantically different)
  const handleDescriptionEdit = useCallback((description: string) => {
    onDescriptionChange(description);
    setShowDescriptionField(true);
    setShowDescriptionCards(false);
  }, [onDescriptionChange, setShowDescriptionField, setShowDescriptionCards]);

  // Handle toggling between description input field and suggestion cards view
  const handleToggleDescriptionView = useCallback(() => {
    // Don't allow toggling if disabled (e.g., channel is already created)
    if (disabled) {
      return;
    }
    
    if (showDescriptionField) {
      // Switch to cards view
      setShowDescriptionField(false);
      setShowDescriptionCards(true);
    } else {
      // Switch to input field view
      setShowDescriptionField(true);
      setShowDescriptionCards(false);
    }
  }, [disabled, showDescriptionField, setShowDescriptionField, setShowDescriptionCards]);

  return {
    handleDescriptionSelect,
    handleDescriptionEdit,
    handleToggleDescriptionView,
  };
};
